import React, { useState } from 'react';
import { User, Phone, Building2, CheckCircle, AlertCircle, UserPlus, X } from 'lucide-react';
import { GlowButton } from '../../components/landing/LandingPrimitives';
import { supportAPI } from '../../services/api';

export default function InquiryModal({ onClose }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [facilityDetails, setFacilityDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !phone.trim() || !facilityDetails.trim()) {
      setError('All fields are required');
      return;
    }

    if (!/^\d{10}$/.test(phone.trim())) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setSubmitting(true);
    try {
      const result = await supportAPI.submitInquiry({
        name: name.trim(),
        phone: phone.trim(),
        facilityDetails: facilityDetails.trim(),
      });
      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to submit inquiry. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card rounded-2xl w-full max-w-md p-6 md:p-8 z-10 max-h-[90vh] overflow-y-auto border border-white/10">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-ga-green/20 rounded-full mb-4">
              <CheckCircle className="h-8 w-8 text-ga-green" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">You are on the list!</h2>
            <p className="text-slate-300 mb-6">
              Thanks for reaching out. We will personally review your facility details and be in touch within 24 hours.
            </p>
            <GlowButton onClick={onClose}>Close</GlowButton>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-ga-primary/20 rounded-full mb-3">
                <UserPlus className="h-7 w-7 text-ga-lime" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">Get early access</h2>
              <p className="text-sm text-slate-400">Tell us about your facility and we will reach out to onboard you personally.</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-xl flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="inq-name" className="block text-sm font-medium text-slate-300 mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 text-slate-500" />
                  </div>
                  <input
                    id="inq-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="landing-input block w-full pl-10 pr-3 py-2.5 text-sm"
                    placeholder="Your name"
                    disabled={submitting}
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label htmlFor="inq-phone" className="block text-sm font-medium text-slate-300 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-4 w-4 text-slate-500" />
                  </div>
                  <input
                    id="inq-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="landing-input block w-full pl-10 pr-3 py-2.5 text-sm"
                    placeholder="Best number to reach you"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="inq-facility" className="block text-sm font-medium text-slate-300 mb-1">
                  About your facility
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-2.5 pointer-events-none">
                    <Building2 className="h-4 w-4 text-slate-500" />
                  </div>
                  <textarea
                    id="inq-facility"
                    value={facilityDetails}
                    onChange={(e) => setFacilityDetails(e.target.value)}
                    rows={3}
                    className="landing-input block w-full pl-10 pr-3 py-2.5 text-sm resize-none"
                    placeholder="e.g. Cricket ground in Pune, 2 pitches, looking to manage bookings and staff"
                    disabled={submitting}
                  />
                </div>
              </div>

              <GlowButton type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-950" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Request Early Access</span>
                )}
              </GlowButton>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
